import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { useAuth } from '../context/AuthContext';
import { dashboardService, DashboardData, ScheduledCall } from '../services/dashboardService';
import { formatDateTime } from '../utils/dateUtils';
import { 
  HiOutlineUserGroup, 
  HiOutlineUsers,
  HiOutlineTrendingUp,
  HiOutlineClipboardList,
  HiOutlineCurrencyDollar,
  HiOutlinePhone,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle
} from 'react-icons/hi';
import { 
  FiPhone, 
  FiCalendar, 
  FiClock, 
  FiArrowRight,
  FiAlertCircle,
  FiCheckCircle,
  FiX
} from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('all');
  const [dashboardYear, setDashboardYear] = useState(() => new Date().getFullYear());
  const [dashboardMonth, setDashboardMonth] = useState(() => new Date().getMonth() + 1);

  const dashboardMonthYearInitialized = useRef(false);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const usesMonthYearFilter = user?.role === 'org_admin' || user?.role === 'manager' || user?.role === 'executive';

  const loadDashboard = async (paramsOverride?: { year: number; month: number }) => {
    try {
      setLoading(true);
      const params = usesMonthYearFilter
        ? (paramsOverride ?? { year: dashboardYear, month: dashboardMonth })
        : undefined;
      const data = await dashboardService.getDashboard(params);
      setDashboardData(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!usesMonthYearFilter) return;
    if (!dashboardMonthYearInitialized.current) {
      dashboardMonthYearInitialized.current = true;
      return;
    }
    loadDashboard({ year: dashboardYear, month: dashboardMonth });
  }, [dashboardYear, dashboardMonth, user?.role]);

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '₹0';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const formatDateTimeDisplay = (dateString: string, timeString?: string) =>
    formatDateTime(dateString, timeString);

  const getCallStatusBadge = (call: ScheduledCall) => {
    if (call.is_missed) {
      return (
        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-300 animate-pulse">
          Missed
        </span>
      );
    }
    if (call.status === 'completed') {
      return (
        <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
          Completed
        </span>
      );
    }
    if (call.status === 'in_progress') {
      return (
        <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300">
          In Progress
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300 animate-pulse">
        Pending
      </span>
    );
  };

  const getEntityLink = (call: ScheduledCall) => {
    if (call.entity_type === 'lead') {
      return `/leads/${call.entity_id}`;
    } else if (call.entity_type === 'customer') {
      return `/customers/${call.entity_id}`;
    } else if (call.entity_type === 'tender') {
      return `/tenders/${call.entity_id}`;
    }
    return '#';
  };

  const filteredCalls = dashboardData?.today_scheduled_calls.filter(call => {
    if (selectedUserFilter === 'all') return true;
    const userName = call.assigned_to_name || call.called_by_name || '';
    return userName.toLowerCase().includes(selectedUserFilter.toLowerCase());
  }) || [];

  const missedCalls = filteredCalls.filter(call => call.is_missed);
  const pendingCalls = filteredCalls.filter(call => !call.is_missed && call.status !== 'completed');

  // Chart data
  const customerStatusData = (dashboardData?.customer_status_distribution || []).map(item => ({
    name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
    value: item.count
  }));

  const COLORS = {
    active: '#10b981',
    inactive: '#6b7280',
    dormant: '#f59e0b',
    lost: '#ef4444',
    customer: '#8b5cf6'
  };

  const getStatusColor = (status: string) => {
    return COLORS[status as keyof typeof COLORS] || '#6b7280';
  };

  const monthYearFilters = (
    <div className="flex items-center gap-2">
      <select
        value={dashboardMonth}
        onChange={(e) => setDashboardMonth(Number(e.target.value))}
        className="text-sm border border-gray-300 rounded px-2 py-1"
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
          <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'short' })}</option>
        ))}
      </select>
      <select
        value={dashboardYear}
        onChange={(e) => setDashboardYear(Number(e.target.value))}
        className="text-sm border border-gray-300 rounded px-2 py-1"
      >
        {[new Date().getFullYear(), new Date().getFullYear() - 1].map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );

  const executiveTargetChartData = dashboardData?.target_summary
    ? [{
        name: user?.name || 'You',
        target: dashboardData.target_summary.month_target,
        achieved: dashboardData.target_summary.month_achieved,
      }]
    : [];

  if (loading && !dashboardData) {
    return (
      <Layout>
        <div className="text-center py-8">Loading dashboard...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-black-950 via-black-900 to-black-800 border-2 border-yellow-500 rounded-lg shadow-md p-4 sm:p-5 text-yellow-400">
          <h1 className="text-xl sm:text-2xl font-bold mb-1 text-yellow-400">Welcome back, {user?.name}! 👋</h1>
          <p className="text-yellow-300 text-sm sm:text-base">
            {user?.role === 'org_admin' && '🎯 Organization Administrator'}
            {user?.role === 'manager' && '👔 Manager Dashboard'}
            {user?.role === 'executive' && '💼 Executive Dashboard'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Metrics Cards */}
        {dashboardData && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Admin Dashboard Metrics */}
              {user?.role === 'org_admin' && (
                <>
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Icon component={HiOutlineUsers} className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Total Customers</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.total_customers || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{(dashboardData.metrics.active_customers || 0)} active</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Icon component={HiOutlineTrendingUp} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Total Data</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.total_leads || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{(dashboardData.metrics.pending_leads || 0)} pending</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Icon component={HiOutlineClipboardList} className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Active Orders</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.active_orders || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <Icon component={HiOutlineCurrencyDollar} className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Monthly Revenue</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.metrics.monthly_revenue)}</p>
                    <p className="text-xs text-gray-500 mt-1">{(dashboardData.metrics.conversion_rate || 0)}% conversion</p>
                  </div>
                </>
              )}

              {/* Manager Dashboard Metrics */}
              {user?.role === 'manager' && (
                <>
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Icon component={HiOutlineUsers} className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Team Customers</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.team_customers || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Icon component={HiOutlineTrendingUp} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Team Leads</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.team_leads || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <Icon component={HiOutlineTrendingUp} className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">Team Tenders</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.team_tenders || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Icon component={HiOutlineClipboardList} className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">My Orders</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {dashboardData.metrics.my_orders_this_month ?? 0}/{dashboardData.metrics.my_orders_total ?? 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">this month / total</p>
                  </div>
                </>
              )}

              {/* Executive Dashboard Metrics */}
              {user?.role === 'executive' && (
                <>
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Icon component={HiOutlineUsers} className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">My Customers</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.my_customers || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Icon component={HiOutlineTrendingUp} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">My Leads</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.my_leads || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <Icon component={HiOutlineTrendingUp} className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">My Tenders</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.metrics.my_tenders || 0}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Icon component={HiOutlineClipboardList} className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">My Orders</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {dashboardData.metrics.my_orders_this_month ?? 0}/{dashboardData.metrics.my_orders_total ?? 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">this month / total</p>
                  </div>
                </>
              )}
            </div>

            {/* Manager: Target vs achieved by brand and by sales executive */}
            {user?.role === 'manager' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-lg font-bold text-gray-900">Target vs achieved (tons)</h2>
                  {monthYearFilters}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 border border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-4">By brand</h3>
                    {dashboardData.target_vs_achievement_by_brand?.brands &&
                    dashboardData.target_vs_achievement_by_brand.brands.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={dashboardData.target_vs_achievement_by_brand.brands}
                          margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="target" fill="#f59e0b" name="Target (tons)" />
                          <Bar dataKey="achieved" fill="#10b981" name="Achieved (tons)" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500">No brand target data for this month</div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 border border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-4">By sales executive</h3>
                    {dashboardData.target_vs_achievement_by_executive?.users &&
                    dashboardData.target_vs_achievement_by_executive.users.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={dashboardData.target_vs_achievement_by_executive.users}
                          margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="target" fill="#f59e0b" name="Target (tons)" />
                          <Bar dataKey="achieved" fill="#10b981" name="Achieved (tons)" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500">No executive target data for this month</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Executive: Monthly target & achievement (current month, by week) */}
            {user?.role === 'executive' && dashboardData.target_summary && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Target vs achieved (tons)</h3>
                  {monthYearFilters}
                </div>
                {executiveTargetChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={executiveTargetChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="target" fill="#f59e0b" name="Target (tons)" />
                      <Bar dataKey="achieved" fill="#10b981" name="Achieved (tons)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">No target/achievement data for this month</div>
                )}
                <h2 className="text-lg font-bold text-gray-900 mb-3 mt-6">Weekly breakdown</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-gray-600">Month target</p>
                    <p className="text-xl font-bold text-amber-800">{Number(dashboardData.target_summary.month_target).toLocaleString()} tons</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-gray-600">Month achieved</p>
                    <p className="text-xl font-bold text-green-800">{Number(dashboardData.target_summary.month_achieved).toLocaleString()} tons</p>
                  </div>
                </div>
                {dashboardData.target_summary.weeks && dashboardData.target_summary.weeks.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 font-medium text-gray-700">Week</th>
                          <th className="text-left py-2 font-medium text-gray-700">Period</th>
                          <th className="text-right py-2 font-medium text-gray-700">Target (tons)</th>
                          <th className="text-right py-2 font-medium text-gray-700">Achieved (tons)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.target_summary.weeks.map((w) => (
                          <tr key={w.week} className="border-b border-gray-100">
                            <td className="py-2">Week {w.week}</td>
                            <td className="py-2 text-gray-600">{w.start_date} – {w.end_date}</td>
                            <td className="py-2 text-right">{Number(w.weekly_target).toLocaleString()}</td>
                            <td className="py-2 text-right">{Number(w.achieved).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Admin: Target vs Achievement chart */}
            {user?.role === 'org_admin' && dashboardData.target_vs_achievement && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Target vs achieved (tons)</h3>
                  {monthYearFilters}
                </div>
                {dashboardData.target_vs_achievement.users && dashboardData.target_vs_achievement.users.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardData.target_vs_achievement.users} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="target" fill="#f59e0b" name="Target (tons)" />
                      <Bar dataKey="achieved" fill="#10b981" name="Achieved (tons)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">No target/achievement data for this month</div>
                )}
              </div>
            )}

            {/* Today's Scheduled Calls Widget - PROMINENT */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 border-2 border-yellow-300 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Icon component={FiPhone} className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    Today's Scheduled Calls
                  </h2>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                    {filteredCalls.length}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {missedCalls.length > 0 && (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 animate-pulse">
                      {missedCalls.length} Missed
                    </span>
                  )}
                  <select
                    value={selectedUserFilter}
                    onChange={(e) => setSelectedUserFilter(e.target.value)}
                    className="text-xs sm:text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <option value="all">All Users</option>
                    {(dashboardData.user_interactions || []).map((ui) => (
                      <option key={ui.id} value={ui.name}>{ui.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Call Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                  <div className="text-lg sm:text-xl font-bold text-blue-600">{filteredCalls.length}</div>
                  <div className="text-xs text-gray-600">Total Scheduled</div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    {filteredCalls.filter(c => c.status === 'completed').length}
                  </div>
                  <div className="text-xs text-gray-600">Completed</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <div className="text-lg sm:text-xl font-bold text-yellow-600">{pendingCalls.length}</div>
                  <div className="text-xs text-gray-600">Pending</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2 sm:p-3 text-center">
                  <div className="text-lg sm:text-xl font-bold text-red-600">{missedCalls.length}</div>
                  <div className="text-xs text-gray-600">Missed</div>
                </div>
              </div>

              {/* Missed Calls Section - RED HIGHLIGHT */}
              {missedCalls.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon component={FiAlertCircle} className="w-5 h-5 text-red-600" />
                    <h3 className="font-bold text-red-900">Missed Calls ({missedCalls.length})</h3>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {missedCalls.map((call) => (
                      <div key={`${call.call_type}-${call.id}`} className="bg-white rounded p-2 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <Link
                              to={getEntityLink(call)}
                              className="font-semibold text-sm text-gray-900 hover:text-indigo-600"
                            >
                              {call.entity_name}
                            </Link>
                            <div className="text-xs text-gray-600">{call.contact_no}</div>
                            <div className="text-xs text-red-600">
                              Scheduled: {formatDateTimeDisplay(call.scheduled_date, call.scheduled_time)}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getCallStatusBadge(call)}
                            <button
                              onClick={() => navigate(getEntityLink(call))}
                              className="px-2 py-1 bg-yellow-500 text-black-950 rounded text-xs hover:bg-yellow-600 font-bold"
                            >
                              Call Now
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Scheduled Calls */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredCalls.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">No calls scheduled for today</div>
                ) : (
                  filteredCalls.map((call) => (
                    <div
                      key={`${call.call_type}-${call.id}`}
                      className={`p-3 rounded-lg border ${
                        call.is_missed
                          ? 'bg-red-50 border-red-200'
                          : call.status === 'completed'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Link
                              to={getEntityLink(call)}
                              className="font-semibold text-sm text-gray-900 hover:text-indigo-600"
                            >
                              {call.entity_name}
                            </Link>
                            {getCallStatusBadge(call)}
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex items-center space-x-1">
                              <Icon component={FiPhone} className="w-3 h-3" />
                              <span>{call.contact_no}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Icon component={FiClock} className="w-3 h-3" />
                              <span>{formatDateTimeDisplay(call.scheduled_date, call.scheduled_time)}</span>
                            </div>
                            {(call.assigned_to_name || call.called_by_name) && (
                              <div className="flex items-center space-x-1">
                                <Icon component={HiOutlineUserGroup} className="w-3 h-3" />
                                <span>{call.assigned_to_name || call.called_by_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(getEntityLink(call))}
                          className="ml-2 px-3 py-1.5 bg-yellow-500 text-black-950 rounded text-xs hover:bg-yellow-600 flex items-center space-x-1 font-bold"
                        >
                          <Icon component={FiPhone} className="w-3 h-3" />
                          <span>View</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Charts Row - Only show for admin users */}
            {(user?.role === 'org_admin' && (dashboardData.customer_status_distribution || dashboardData.user_interactions)) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {/* Customer Status Distribution */}
                {dashboardData.customer_status_distribution && (
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Customer Status Distribution</h3>
                    {customerStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={customerStatusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {customerStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getStatusColor(entry.name.toLowerCase())} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500">No data available</div>
                    )}
                  </div>
                )}

                {/* User Interaction Overview */}
                {dashboardData.user_interactions && (
                  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">User Call Activity Today</h3>
                    {dashboardData.user_interactions.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={dashboardData.user_interactions}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="calls_completed_today" fill="#3b82f6" name="Completed" />
                          <Bar dataKey="scheduled_calls_pending" fill="#f59e0b" name="Pending" />
                          <Bar dataKey="scheduled_calls_missed" fill="#ef4444" name="Missed" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500">No data available</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Recent Activities - Only show for admin users */}
            {user?.role === 'org_admin' && dashboardData.recent_activities && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Recent Activities</h3>
                <div className="space-y-2">
                  {dashboardData.recent_activities.length > 0 ? (
                    dashboardData.recent_activities.map((activity, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 border-b border-gray-100 last:border-0">
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'order' ? 'bg-orange-100' : 'bg-blue-100'
                      }`}>
                        <Icon 
                          component={activity.type === 'order' ? HiOutlineClipboardList : FiPhone} 
                          className={`w-4 h-4 ${
                            activity.type === 'order' ? 'text-orange-600' : 'text-blue-600'
                          }`} 
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500">{formatDateTimeDisplay(activity.activity_date)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">No recent activities</div>
                )}
              </div>
            </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
